import React, { useEffect, useMemo, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { listSeasons } from "../graphql/queries";
import { createSeason, deleteSeason, updateSeason } from "../graphql/mutations";
import { formatApiError } from "../lib/formatApiError";
import AppDialog from "../components/AppDialog";

interface Season {
  id: string;
  name: string;
  year: number;
  startDate: string;
  endDate: string;
}

interface SeasonsPageProps {
  userRoles: string[];
}

type SortKey = "name" | "year" | "startDate" | "endDate";

const SeasonsPage: React.FC<SeasonsPageProps> = ({ userRoles }) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("year");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [name, setName] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingYear, setEditingYear] = useState(String(new Date().getFullYear()));
  const [editingStartDate, setEditingStartDate] = useState("");
  const [editingEndDate, setEditingEndDate] = useState("");

  const canManage = userRoles.includes("SuperAdmin") || userRoles.includes("Commish");

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const res: any = await API.graphql(graphqlOperation(listSeasons, { limit: 300 }));
      setSeasons(((res?.data?.listSeasons?.items ?? []).filter(Boolean) as Season[]));
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load seasons: ${formatApiError(error)}`);
      }
    }
  };

  const resetCreate = () => {
    setName("");
    setYear(String(new Date().getFullYear()));
    setStartDate("");
    setEndDate("");
  };

  const handleCreate = async () => {
    const parsedYear = Number(year);
    if (!name.trim() || !Number.isFinite(parsedYear) || !startDate || !endDate) {
      setErrorMessage("Season name, year, start date, and end date are required.");
      return;
    }
    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(createSeason, {
          input: {
            name: name.trim(),
            year: parsedYear,
            startDate,
            endDate
          }
        })
      );
      if (!result?.data?.createSeason) {
        throw new Error(formatApiError(result));
      }
      setCreateOpen(false);
      resetCreate();
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to create season: ${formatApiError(error)}`);
    }
  };

  const handleUpdate = async () => {
    if (!editingId) {
      return;
    }
    const parsedYear = Number(editingYear);
    if (!editingName.trim() || !Number.isFinite(parsedYear) || !editingStartDate || !editingEndDate) {
      setErrorMessage("Season name, year, start date, and end date are required.");
      return;
    }
    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(updateSeason, {
          input: {
            id: editingId,
            name: editingName.trim(),
            year: parsedYear,
            startDate: editingStartDate,
            endDate: editingEndDate
          }
        })
      );
      if (!result?.data?.updateSeason) {
        throw new Error(formatApiError(result));
      }
      setEditOpen(false);
      setEditingId(null);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to update season: ${formatApiError(error)}`);
    }
  };

  const handleDelete = async (season: Season) => {
    if (!window.confirm(`Delete season "${season.name} ${season.year}"?`)) {
      return;
    }
    try {
      setErrorMessage("");
      const result: any = await API.graphql(graphqlOperation(deleteSeason, { input: { id: season.id } }));
      if (!result?.data?.deleteSeason) {
        throw new Error(formatApiError(result));
      }
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to delete season: ${formatApiError(error)}`);
    }
  };

  const rows = useMemo(() => {
    const normalizedText = filterText.trim().toLowerCase();
    const normalizedYear = filterYear.trim();
    const filtered = seasons.filter((season) => {
      if (normalizedText && !`${season.name} ${season.year}`.toLowerCase().includes(normalizedText)) {
        return false;
      }
      if (normalizedYear && String(season.year) !== normalizedYear) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      let compare = 0;
      if (sortKey === "name") {
        compare = a.name.localeCompare(b.name);
      } else if (sortKey === "startDate") {
        compare = a.startDate.localeCompare(b.startDate);
      } else if (sortKey === "endDate") {
        compare = a.endDate.localeCompare(b.endDate);
      } else {
        compare = a.year - b.year;
      }
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [filterText, filterYear, seasons, sortDirection, sortKey]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Seasons</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {canManage && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button
            onClick={() => {
              resetCreate();
              setCreateOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add Season
          </button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Filter by season name"
        />
        <input
          value={filterYear}
          onChange={(event) => setFilterYear(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Filter by year (2026)"
        />
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleSort("name")}>Season</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("year")}>Year</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("startDate")}>Start Date</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("endDate")}>End Date</button></th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="text-gray-600">No seasons found.</td>
              </tr>
            )}
            {rows.map((season) => (
              <tr key={season.id}>
                <td>{season.name}</td>
                <td>{season.year}</td>
                <td>{season.startDate}</td>
                <td>{season.endDate}</td>
                {canManage && (
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="bg-yellow-500 text-white px-3 py-1 rounded"
                        onClick={() => {
                          setEditingId(season.id);
                          setEditingName(season.name);
                          setEditingYear(String(season.year));
                          setEditingStartDate(season.startDate);
                          setEditingEndDate(season.endDate);
                          setEditOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button className="bg-red-600 text-white px-3 py-1 rounded" onClick={() => handleDelete(season)}>
                        Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add Season"
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleCreate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="border px-3 py-2 rounded"
            placeholder="Season name (ex. Rec Spring, Travel Fall)"
          />
          <input
            type="number"
            min={2000}
            max={2100}
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="border px-3 py-2 rounded"
            placeholder="Year"
          />
          <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="border px-3 py-2 rounded" />
          <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="border px-3 py-2 rounded" />
        </div>
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Season"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleUpdate} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            value={editingName}
            onChange={(event) => setEditingName(event.target.value)}
            className="border px-3 py-2 rounded"
            placeholder="Season name"
          />
          <input
            type="number"
            min={2000}
            max={2100}
            value={editingYear}
            onChange={(event) => setEditingYear(event.target.value)}
            className="border px-3 py-2 rounded"
            placeholder="Year"
          />
          <input type="date" value={editingStartDate} onChange={(event) => setEditingStartDate(event.target.value)} className="border px-3 py-2 rounded" />
          <input type="date" value={editingEndDate} onChange={(event) => setEditingEndDate(event.target.value)} className="border px-3 py-2 rounded" />
        </div>
      </AppDialog>
    </div>
  );
};

export default SeasonsPage;
